import type { LensCard, LensDataset, LensFilter } from '~/types/leLens';

import { datasetHasColumn } from './filterColumnCoverage';

/**
 * New dashboard filters apply to every query-backed card. The reporting API
 * wraps each query using the bound result column, so card SQL stays valid for
 * different query shapes while receiving the same dashboard selection.
 */
export function bindFilterToCards(
  cards: LensCard[],
  filter: LensFilter,
  datasets?: ReadonlyArray<LensDataset>,
): LensCard[] {
  if (!filter.column) return cards;

  const binding = { type: filter.type, column: filter.column };

  return cards.map((card) => {
    if (card.type === 'insight' || !card.datasetId) return card;
    if (card.filterBindings?.[filter.id]) return card;
    if (
      datasets &&
      !datasets.some((dataset) => dataset.id === card.datasetId && datasetHasColumn(dataset, filter.column!))
    )
      return card;

    return {
      ...card,
      filterBindings: { ...card.filterBindings, [filter.id]: binding },
    };
  });
}

/**
 * Removing a dashboard filter must also remove its per-card bindings. Keeping
 * one produces an invalid Lens spec because a binding may only reference an
 * existing dashboard filter.
 */
export function unbindFilterFromCards(cards: LensCard[], filterId: string): LensCard[] {
  return cards.map((card) => {
    if (!card.filterBindings?.[filterId]) return card;

    const { [filterId]: _removedBinding, ...filterBindings } = card.filterBindings;
    return {
      ...card,
      ...(Object.keys(filterBindings).length ? { filterBindings } : { filterBindings: undefined }),
    };
  });
}
